import { computed, getCurrentScope, onBeforeUnmount, onScopeDispose, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { v4 as uuid } from 'uuid';

export type LockStore = ReturnType<typeof createLockStore>;

/**
 * Creates a new Pinia store for a lockable, boolean state value. Useful for things like coordinating
 * a single loading state that can be triggered in multiple places, asynchronously.
 *
 * @example
 *
 * const useLoaderStore = createLockStore('page-loader');
 *
 * // later...
 *
 * const loaderStore = useLoaderStore("page-loader");
 *
 * // loaderStore.locked === false;
 *
 * // Imagine that these live in separate components that fire off requests independently and a single, global
 * // loader needs to be shown until both requests have resolved
 * const loadingProductsSymbol = symbol('products');
 * const loadingCustomerSymbol = symbol('customer');
 *
 * // start loading products
 * loaderStore.lock(loadingProductsSymbol);
 * // loaderStore.locks === Set(loadingProductsSymbol);
 * // loaderStore.locked === true;
 *
 * // start loading customer
 * loaderStore.lock(loadingCustomerSymbol);
 * // loaderStore.locks === Set(loadingProductsSymbol, loadingCustomerSymbol);
 * // still, loaderStore.locked === true;
 *
 * // products resolved, customer still loading
 * loaderStore.unlock(loadingProductsSymbol);
 * // loaderStore.locks === Set(loadingCustomerSymbol);
 * // still, loaderStore.locked === true;
 *
 * // customer resolved
 * loaderStore.unlock(loadingCustomerSymbol);
 * // loaderStore.locks === Set();
 * // loaderStore.locked === false;
 *
 * @param name Name of the store passed to Pinia
 * @returns
 */
export function createLockStore(name: string) {
	return defineStore(name, () => {
		const locks = ref(new Set<symbol>());
		const locked = computed(() => locks.value.size > 0);

		return {
			/**
			 * Whether or not there are any locks held
			 */
			locked: locked,

			/**
			 * Restricted interface to the locks Set for determining whether or not a given key has a lock held
			 */
			locks: computed(() => locks.value as Pick<Set<symbol>, "has">),

			/**
			 * Apply a lock
			 * @param key Unique symbol that identifies a lock
			 */
			lock(key: symbol) {
				locks.value = new Set(locks.value.add(key));
			},

			/**
			 * Remove a lock
			 * @param key Unique symbol that identifies a lock
			 */
			unlock(key: symbol) {
				locks.value.delete(key);
				locks.value = new Set(locks.value);
			},

			/**
			 * Removes all currently-held locks
			 */
			unlockAll() {
				locks.value = new Set();
			},
		};
	});
}

interface LockStoreComposableOptions {
	/**
	 * Readable name for the symbol that goes into the LockStore behind the scenes
	 */
	name?: string;

	/**
	 * Release lock when component is destroyed. Defaults to true.
	 */
	cleanup?: boolean
}

/**
 * Creates a composable exposing a boolean ref interface to a LockStore
 *
 * @param store
 * @returns
 */
export function createLockStoreComposable(store: LockStore) {
	/**
	 * Creates a boolean ref that controls the lock state for a given key in the store
	 *
	 * @param options
	 * @returns
	 */
	function composable({ name = "", cleanup = true }: LockStoreComposableOptions = {}) {
		const key = Symbol(name + uuid());

		const lockStore = store();

		if (cleanup && getCurrentScope()) {
			onScopeDispose(() => {
				lockStore.unlock(key);
			});
		}

		return computed({
			get: () => lockStore.locks.has(key),
			set: isKeyLocked => {
				if (isKeyLocked) {
					lockStore.lock(key);
				} else {
					lockStore.unlock(key);
				}
			},
		});
	};

	return composable;
}